// utils/cacheBust.js

const STORAGE_KEY = 'force_reload_timestamp'
const RELOAD_COOLDOWN = 30000 // 30 seconds — enough time for the page to load

/**
 * Clears all service worker registrations and cache storage,
 * then performs a hard reload of the current page.
 *
 * @returns {Promise<boolean>} false if a reload loop was detected, true otherwise
 */
export async function reloadWithCacheBust () {
	if (isReloadLoopDetected()) {
		console.error('[cacheBust] Reload loop detected, stopping.')
		return false
	}

	recordReloadTimestamp()
	await clearAllCaches()
	window.location.reload()
	return true
}

function isReloadLoopDetected () {
	try {
		const lastReload = sessionStorage.getItem(STORAGE_KEY)
		if (!lastReload) return false
		return Date.now() - parseInt(lastReload, 10) < RELOAD_COOLDOWN
	} catch {
		return false
	}
}

function recordReloadTimestamp () {
	try {
		sessionStorage.setItem(STORAGE_KEY, Date.now().toString())
	} catch (err) {
		console.warn('[cacheBust] Failed to record reload timestamp:', err)
	}
}

async function clearAllCaches () {
	// Unregister service workers first so they can't intercept
	// the upcoming reload and serve stale content
	if ('serviceWorker' in navigator) {
		try {
			const registrations = await navigator.serviceWorker.getRegistrations()
			await Promise.all(registrations.map(reg => reg.unregister()))
		} catch (err) {
			console.error('[cacheBust] Failed to unregister service workers:', err)
		}
	}

	// Delete all cache storage entries
	if ('caches' in window) {
		try {
			const cacheNames = await caches.keys()
			await Promise.all(cacheNames.map(name => caches.delete(name)))
		} catch (err) {
			console.error('[cacheBust] Failed to clear cache storage:', err)
		}
	}
}
